if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.policyCheckbox = this.form.querySelector('[data-consult-policy]');
        this.policyError = this.form.querySelector('[data-consult-policy-error]');

        if (this.policyCheckbox) {
          this.policyCheckbox.addEventListener('change', this.updatePolicyState.bind(this));
          this.form.addEventListener('click', this.onPolicyProtectedAction.bind(this), true);
          this.form.addEventListener('keydown', this.onPolicyProtectedKeydown.bind(this), true);

          this.policyButtonObserver = new MutationObserver(() => this.updatePolicyState());
          this.policyButtonObserver.observe(this.form, { childList: true, subtree: true });

          this.updatePolicyState();
        }

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      showPolicyError() {
        if (this.policyError) this.policyError.hidden = false;
        this.policyCheckbox.setAttribute('aria-invalid', 'true');
        this.policyCheckbox.focus();
      }

      onPolicyProtectedAction(evt) {
        if (this.policyCheckbox.checked) return;

        const protectedAction = evt.target.closest('button[type="submit"], #apntly-book-button');
        if (!protectedAction || !this.form.contains(protectedAction)) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.showPolicyError();
      }

      onPolicyProtectedKeydown(evt) {
        if (this.policyCheckbox.checked || !['Enter', ' '].includes(evt.key)) return;

        const protectedAction = evt.target.closest('#apntly-book-button');
        if (!protectedAction || !this.form.contains(protectedAction)) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();
        this.showPolicyError();
      }

      updatePolicyState() {
        if (!this.policyCheckbox) return;

        const policyAccepted = this.policyCheckbox.checked;
        const variantInput = this.form.querySelector('[name=id]');
        const nativeButtons = this.form.querySelectorAll('button[type="submit"]');
        const bookingButtons = this.form.querySelectorAll('#apntly-book-button');

        this.policyCheckbox.setAttribute('aria-invalid', 'false');
        if (this.policyError) this.policyError.hidden = true;

        nativeButtons.forEach((button) => {
          if (!policyAccepted) {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
          } else if (!variantInput.disabled) {
            button.disabled = false;
            button.removeAttribute('aria-disabled');
          }
        });

        bookingButtons.forEach((button) => {
          button.classList.toggle('consult-policy-blocked', !policyAccepted);
          button.setAttribute('aria-disabled', policyAccepted ? 'false' : 'true');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.policyCheckbox && !this.policyCheckbox.checked) {
          this.showPolicyError();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const textSpan = this.submitButton.querySelector('span:not(.sold-out-message)');
        const originalText = textSpan ? textSpan.innerText : null;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span')?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }

            // ==============================
            // ADDED ✓ STATE
            // ==============================
            if (textSpan && originalText) {
              textSpan.innerText = 'Added ✓';
              this.submitButton.classList.add('is-added');

              // Reset when cart drawer closes
              document.addEventListener(
                'cart:closed',
                () => {
                  textSpan.innerText = originalText;
                  this.submitButton.classList.remove('is-added');
                },
                { once: true }
              );
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
